import axios, { AxiosError } from 'axios';
import { atlasAuthApi } from '../../services/api/atlasAuthApi';

// ─── Errors ───────────────────────────────────────────────────────────────────

export function readAxiosDetail(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { detail?: unknown } | undefined;
    if (data && typeof data.detail === 'string') return data.detail;
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Request failed';
}

// ─── Types (backend: FeaturePlans/frontend-integration-guide.md) ───────────

export interface AdminFeatureFlag {
  feature_flag_id: string;
  name: string;
  description: string;
  enabled: boolean;
  config: Record<string, unknown>;
  created_at: string;
}

export interface AdminUser {
  user_id: string;
  email: string;
  full_name: string;
  role: 'user' | 'admin';
  disabled: boolean;
  created_at: string;
  customer_id: string | null;
  customer_name: string | null;
}

interface ApiAdminUser {
  id: string;
  username: string;
  full_name: string;
  email: string;
  disabled: boolean;
  is_admin: boolean | 'true' | 'false' | null;
  customer_id: string | null;
  created_at: string;
  customer_name: string | null;
}

export function normalizeIsAdminValue(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return false;
}

export function mapApiUser(u: ApiAdminUser): AdminUser {
  return {
    user_id: u.id,
    email: (u.email || u.username || '').trim(),
    full_name: u.full_name || '',
    role: normalizeIsAdminValue(u.is_admin) ? 'admin' : 'user',
    disabled: u.disabled,
    created_at: u.created_at,
    customer_id: u.customer_id,
    customer_name: u.customer_name,
  };
}

export interface AdminCustomer {
  customer_id: string;
  name: string;
  created_at: string;
  /** Org-wide default transit network (opaque GTFS feed id), cascades to the customer's users. */
  default_network_feed_id?: string | null;
}

export interface CustomerFeatureFlagOverride {
  customer_feature_flag_id: string;
  customer_id: string;
  feature_flag_id: string;
  enabled: boolean;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  flag_name: string;
}

export interface AdminLicense {
  license_id: string;
  customer_id: string;
  user_id: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
  user_email: string | null;
  customer_name: string;
}

// ─── Feature flags (global) ─────────────────────────────────────────────────

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

/**
 * Extracts raw flag rows from various API envelope shapes (direct array vs `{ flags }`, etc.).
 */
export function extractAdminFeatureFlagRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const rec = asRecord(payload);
  if (!rec) return [];
  const keys = ['flags', 'items', 'data', 'results', 'feature_flags'] as const;
  for (const k of keys) {
    const v = rec[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

/**
 * Maps one API row to our `AdminFeatureFlag` shape (tolerates `id` vs `feature_flag_id`, wrapped POST bodies).
 */
export function normalizeAdminFeatureFlagRow(raw: unknown): AdminFeatureFlag | null {
  const r = asRecord(raw);
  if (!r) return null;
  const idRaw = r.feature_flag_id ?? r.id ?? r.flag_id;
  const id = idRaw != null && String(idRaw).trim() !== '' ? String(idRaw) : '';
  const nameRaw = r.name ?? r.flag_name ?? r.key;
  const name = typeof nameRaw === 'string' && nameRaw.trim() !== '' ? nameRaw.trim() : '';
  if (!id || !name) return null;

  const description = typeof r.description === 'string' ? r.description : '';
  const enabled =
    r.enabled === true ||
    r.enabled === 'true' ||
    r.default_enabled === true ||
    r.default_enabled === 'true';

  let config: Record<string, unknown> = {};
  if (r.config && typeof r.config === 'object' && !Array.isArray(r.config)) {
    config = r.config as Record<string, unknown>;
  }

  const created_at =
    typeof r.created_at === 'string' && r.created_at.trim() !== ''
      ? r.created_at
      : typeof r.updated_at === 'string' && r.updated_at.trim() !== ''
        ? r.updated_at
        : new Date().toISOString();

  return {
    feature_flag_id: id,
    name,
    description,
    enabled: Boolean(enabled),
    config,
    created_at,
  };
}

export function normalizeAdminFeatureFlagsPayload(payload: unknown): AdminFeatureFlag[] {
  return extractAdminFeatureFlagRows(payload)
    .map(normalizeAdminFeatureFlagRow)
    .filter((row): row is AdminFeatureFlag => row != null);
}

export async function adminListFeatureFlags(): Promise<AdminFeatureFlag[]> {
  const res = await atlasAuthApi.get<unknown>('/admin/feature-flags');
  return normalizeAdminFeatureFlagsPayload(res.data);
}

// ─── Users ────────────────────────────────────────────────────────────────────

export async function adminListUsers(params?: {
  customer_id?: string;
  search?: string;
}): Promise<AdminUser[]> {
  const res = await atlasAuthApi.get<ApiAdminUser[]>('/admin/users', { params });
  const rows = Array.isArray(res.data) ? res.data : [];
  return rows.map(mapApiUser);
}

export async function adminUpdateUser(
  userId: string,
  payload: Partial<{ is_admin: boolean; customer_id: string | null; disabled: boolean; company_role: 'company_admin' | 'member' }>
): Promise<AdminUser> {
  const res = await atlasAuthApi.put<ApiAdminUser>(`/admin/users/${userId}`, payload);
  return mapApiUser(res.data);
}

// ─── Customers ────────────────────────────────────────────────────────────────

export async function adminListCustomers(): Promise<AdminCustomer[]> {
  const res = await atlasAuthApi.get<AdminCustomer[]>('/admin/customers');
  return Array.isArray(res.data) ? res.data : [];
}

export async function adminCreateCustomer(name: string): Promise<AdminCustomer> {
  const res = await atlasAuthApi.post<AdminCustomer>('/admin/customers', { name });
  return res.data;
}

/** Set (or clear, with null) a customer's org-wide default transit network. */
export async function adminSetCustomerDefaultNetwork(
  customerId: string,
  feedId: string | null
): Promise<AdminCustomer> {
  const res = await atlasAuthApi.put<AdminCustomer>(`/admin/customers/${customerId}`, {
    default_network_feed_id: feedId,
  });
  return res.data;
}

export async function adminListCustomerFeatureFlags(
  customerId: string
): Promise<CustomerFeatureFlagOverride[]> {
  const res = await atlasAuthApi.get<CustomerFeatureFlagOverride[]>(
    `/admin/customers/${customerId}/feature-flags`
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function adminUpsertCustomerFeatureFlag(
  customerId: string,
  payload: { feature_flag_id: string; enabled: boolean; config: Record<string, unknown> }
): Promise<CustomerFeatureFlagOverride> {
  const res = await atlasAuthApi.put<CustomerFeatureFlagOverride>(
    `/admin/customers/${customerId}/feature-flags`,
    payload
  );
  return res.data;
}

export async function adminDeleteCustomerFeatureFlag(
  customerId: string,
  featureFlagId: string
): Promise<void> {
  await atlasAuthApi.delete(`/admin/customers/${customerId}/feature-flags/${featureFlagId}`);
}

// ─── Licenses ─────────────────────────────────────────────────────────────────

export async function adminListLicenses(params?: { customer_id?: string }): Promise<AdminLicense[]> {
  const res = await atlasAuthApi.get<AdminLicense[]>('/admin/licenses', { params });
  return Array.isArray(res.data) ? res.data : [];
}

export async function adminBatchCreateLicenses(
  customerId: string,
  payload: { count: number; expires_at: string }
): Promise<void> {
  await atlasAuthApi.post(`/admin/customers/${customerId}/licenses`, payload);
}

export async function adminBulkRenewLicenses(
  customerId: string,
  expires_at: string
): Promise<void> {
  await atlasAuthApi.put(`/admin/customers/${customerId}/licenses/renew`, { expires_at });
}

export async function adminRenewLicense(licenseId: string, expires_at: string): Promise<AdminLicense> {
  const res = await atlasAuthApi.put<AdminLicense>(`/admin/licenses/${licenseId}/renew`, { expires_at });
  return res.data;
}

export async function adminAssignLicense(licenseId: string, userId: string): Promise<AdminLicense> {
  const res = await atlasAuthApi.put<AdminLicense>(`/admin/licenses/${licenseId}/assign`, { user_id: userId });
  return res.data;
}

export async function adminRevokeLicense(licenseId: string): Promise<void> {
  await atlasAuthApi.delete(`/admin/licenses/${licenseId}`);
}

// ─── Invitations ─────────────────────────────────────────────────────────────

export interface AdminInvitation {
  invitation_id: string;
  customer_id: string;
  email: string;
  company_role: 'company_admin' | 'member';
  token: string;
  created_by: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export async function adminCreateInvitation(
  customerId: string,
  payload: { email: string; company_role: 'company_admin' | 'member'; expires_in_days: number }
): Promise<AdminInvitation> {
  const res = await atlasAuthApi.post<AdminInvitation>(
    `/admin/customers/${customerId}/invitations`,
    payload
  );
  return res.data;
}

export interface AdminAnalyticsEvent {
  event_name: string;
  session_id: string;
  object_type?: string | null;
  object_id?: string | null;
  user_id?: string | null;
  customer_id?: string | null;
  created_at: string;
  meta?: Record<string, unknown>;
}

export interface AdminAnalyticsSummaryItem {
  category: string;
  event_name: string;
  total_events: number;
  unique_users?: number;
}

export interface AdminAnalyticsActiveUser {
  user_id: string;
  customer_id?: string | null;
  event_count: number;
  last_seen_at: string;
}

export async function adminListAnalyticsEvents(params?: {
  event_name?: string;
  user_id?: string;
  customer_id?: string;
  page?: number;
  page_size?: number;
  from?: string;
  to?: string;
}): Promise<AdminAnalyticsEvent[]> {
  const res = await atlasAuthApi.get<unknown>('/analytics/admin/events', { params });
  const payload = res.data as Record<string, unknown> | unknown[];
  if (Array.isArray(payload)) return payload as AdminAnalyticsEvent[];
  if (payload && typeof payload === 'object') {
    const rows =
      (payload as Record<string, unknown>).events ??
      (payload as Record<string, unknown>).items ??
      (payload as Record<string, unknown>).data;
    if (Array.isArray(rows)) return rows as AdminAnalyticsEvent[];
  }
  return [];
}

export async function adminGetAnalyticsSummary(params?: {
  from?: string;
  to?: string;
}): Promise<AdminAnalyticsSummaryItem[]> {
  const res = await atlasAuthApi.get<unknown>('/analytics/admin/summary', { params });
  const payload = res.data as Record<string, unknown> | unknown[];
  if (Array.isArray(payload)) return payload as AdminAnalyticsSummaryItem[];
  if (payload && typeof payload === 'object') {
    const rows =
      (payload as Record<string, unknown>).summary ??
      (payload as Record<string, unknown>).items ??
      (payload as Record<string, unknown>).data;
    if (Array.isArray(rows)) return rows as AdminAnalyticsSummaryItem[];
  }
  return [];
}

export async function adminListAnalyticsActiveUsers(params?: {
  days?: number;
}): Promise<AdminAnalyticsActiveUser[]> {
  const res = await atlasAuthApi.get<unknown>('/analytics/admin/active-users', { params });
  const payload = res.data as Record<string, unknown> | unknown[];
  if (Array.isArray(payload)) return payload as AdminAnalyticsActiveUser[];
  if (payload && typeof payload === 'object') {
    const rows =
      (payload as Record<string, unknown>).users ??
      (payload as Record<string, unknown>).items ??
      (payload as Record<string, unknown>).data;
    if (Array.isArray(rows)) return rows as AdminAnalyticsActiveUser[];
  }
  return [];
}

export async function adminCleanupAnalytics(payload?: {
  retention_days?: number;
  dry_run?: boolean;
}): Promise<{ deleted?: number; message?: string }> {
  const res = await atlasAuthApi.delete<{ deleted?: number; message?: string }>(
    '/analytics/admin/cleanup',
    { data: payload ?? {} }
  );
  return res.data ?? {};
}
